import { useMemo } from 'react';
import PropTypes from 'prop-types';
import TeamPicker from './TeamPicker';

const collapseTeamTags = (filterTeams, teamOptions, teamGroups) => {
    const displayForRaw = new Map(teamOptions.map(([display, raw]) => [raw, display]));
    const selectedDisplays = new Set(filterTeams.map(r => displayForRaw.get(r)).filter(Boolean));
    const remaining = new Set(filterTeams);
    const tags = [];

    for (const group of teamGroups) {
        const ownedInGroup = group.teams.filter(t => selectedDisplays.has(t));
        if (ownedInGroup.length > 0 && ownedInGroup.length === group.teams.filter(t => {
            const raw = teamOptions.find(([d]) => d === t)?.[1];
            return raw && remaining.has(raw);
        }).length && ownedInGroup.length >= 3) {
            const rawsInGroup = ownedInGroup.map(t => teamOptions.find(([d]) => d === t)?.[1]).filter(Boolean);
            const allInRemaining = rawsInGroup.every(r => remaining.has(r));
            if (allInRemaining) {
                tags.push({ key: `group-${group.label}`, label: group.label, raws: rawsInGroup });
                rawsInGroup.forEach(r => remaining.delete(r));
            }
        }
    }

    for (const raw of remaining) {
        const display = displayForRaw.get(raw);
        if (display) tags.push({ key: `team-${raw}`, label: display, raws: [raw] });
    }

    return tags;
};

const TeamBuilderFilters = ({
    config,
    filterYear,
    setFilterYear,
    filterRating,
    setFilterRating,
    filterTeams,
    setFilterTeams,
    seriesYears,
    teamOptions,
}) => {
    const teamGroups = config?.teamGroups || [];
    const rarityTiers = config?.rarityTiers || [];
    const rarityDisplay = config?.rarityDisplay || {};

    const activeFilterTags = useMemo(() => {
        const tags = [];
        if (filterYear) tags.push({ key: 'year', label: `Year: ${filterYear}`, onRemove: () => setFilterYear('') });
        if (filterRating) {
            const tier = Object.entries(rarityDisplay).find(([, v]) => String(v.rating) === filterRating);
            const label = tier ? `Rating Cap: ${tier[1].label}` : `≤${filterRating}`;
            tags.push({ key: 'rating', label, onRemove: () => setFilterRating('') });
        }

        const teamTags = collapseTeamTags(filterTeams, teamOptions, teamGroups);
        teamTags.forEach(t => {
            tags.push({
                key: t.key,
                label: t.label,
                onRemove: () => setFilterTeams(prev => prev.filter(r => !t.raws.includes(r))),
            });
        });

        return tags;
    }, [filterYear, filterRating, filterTeams, teamOptions, teamGroups, rarityDisplay, setFilterYear, setFilterRating, setFilterTeams]);

    return (
        <>
            {activeFilterTags.length > 0 && (
                <div className="tb-active-filters">
                    {activeFilterTags.map(tag => (
                        <span key={tag.key} className="tb-filter-tag">
                            {tag.label}
                            {tag.onRemove && (
                                <button className="tb-filter-tag__remove" onClick={tag.onRemove}>&times;</button>
                            )}
                        </span>
                    ))}
                </div>
            )}

            <div className="tb-filters">
                <div className="tb-filters__dropdowns">
                    <select value={filterYear} onChange={e => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {seriesYears.map(y => (
                            <option key={y} value={String(y)}>{y}</option>
                        ))}
                    </select>
                    <select value={filterRating} onChange={e => setFilterRating(e.target.value)}>
                        <option value="">All Ratings</option>
                        {rarityTiers.map(tier => {
                            const info = rarityDisplay[tier];
                            if (!info) return null;
                            return (
                                <option key={tier} value={String(info.rating)}>
                                    Up to {info.label} (≤{info.rating})
                                </option>
                            );
                        })}
                    </select>
                </div>
                <TeamPicker
                    value={filterTeams}
                    onChange={setFilterTeams}
                    teamGroups={teamGroups}
                    options={teamOptions}
                />
            </div>
        </>
    );
};

TeamBuilderFilters.propTypes = {
    config: PropTypes.object,
    filterYear: PropTypes.string.isRequired,
    setFilterYear: PropTypes.func.isRequired,
    filterRating: PropTypes.string.isRequired,
    setFilterRating: PropTypes.func.isRequired,
    filterTeams: PropTypes.array.isRequired,
    setFilterTeams: PropTypes.func.isRequired,
    seriesYears: PropTypes.array.isRequired,
    teamOptions: PropTypes.array.isRequired,
};

export default TeamBuilderFilters;
